import React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import Reveal from "../components/Reveal";
import { useLang } from "../contexts/LangContext";
import { api } from "../lib/api";

export default function Formats() {
  const { t, lang } = useLang();
  const [formats, setFormats] = React.useState([]);

  React.useEffect(() => {
    api.get("/formats").then((r) => setFormats(r.data || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="formats-page">
      <PageHeader
        eyebrow={t.formats.eyebrow}
        title={t.formats.title}
        subtitle={t.formats.subtitle}
      />

      <section className="bg-[#0a0a0a] pb-32">
        <div className="container-x space-y-24 lg:space-y-32">
          {formats.map((f, i) => (
            <Reveal key={f.id}>
              <article
                id={f.slug}
                className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center"
                data-testid={`format-detail-${f.slug}`}
              >
                <div className={`lg:col-span-7 zoom-img relative aspect-[16/10] ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                  <img src={f.image_url} alt={f.title_it} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-transparent to-transparent" />
                </div>
                <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                  <div className="font-display text-xs tracking-[0.32em] uppercase text-[color:var(--gold)] mb-4">
                    Format · {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="h2 text-white">{lang === "it" ? f.title_it : f.title_en}</h3>
                  <div className="text-[color:var(--gold)] font-display tracking-wide mt-3 text-lg">
                    {lang === "it" ? f.tagline_it : f.tagline_en}
                  </div>
                  <p className="body-lg mt-6">{lang === "it" ? f.description_it : f.description_en}</p>
                  <div className="mt-7 flex flex-wrap gap-2">
                    {(f.tags || []).map((tag) => (
                      <span key={tag} className="badge-pill">{tag}</span>
                    ))}
                  </div>
                  <Link
                    to="/contatti"
                    className="btn-secondary mt-10"
                    data-testid={`format-cta-${f.slug}`}
                  >
                    {t.common.discoverFormat} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}

export function PageHeader({ eyebrow, title, subtitle, image }) {
  return (
    <section className="relative pt-40 pb-24 lg:pt-48 lg:pb-32 overflow-hidden border-b border-white/5">
      {image && (
        <>
          <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/40 via-[#0a0a0a]/80 to-[#0a0a0a]" />
        </>
      )}
      <div className="relative container-x">
        <Reveal>
          <div className="eyebrow mb-5">{eyebrow}</div>
        </Reveal>
        <Reveal delay={120}>
          <h1 className="h1 text-white max-w-4xl" style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)" }}>
            {title}
          </h1>
        </Reveal>
        {subtitle && (
          <Reveal delay={250}>
            <p className="body-lg mt-6 max-w-2xl">{subtitle}</p>
          </Reveal>
        )}
      </div>
    </section>
  );
}
